import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  Timestamp,
  runTransaction,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from './useAuth';
import type { Invoice, Business, Customer, InventoryItem } from '@/types/invoice';
import { useToast } from '@/components/ui/use-toast';
import { generateInvoiceNumber } from '@/lib/utils';

export function useInvoice() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all invoices
  const fetchInvoices = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const invoicesRef = collection(db, 'users', user.uid, 'invoices');
      const q = query(invoicesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const invoicesList = snapshot.docs.map((doc) => {
        const data = doc.data();
        console.log('Fetched invoice data:', data); // Debug log
        return {
          ...data,
          invoiceId: doc.id,
          issueDate: data.issueDate?.toDate(),
          dueDate: data.dueDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Invoice;
      });
      
      setInvoices(invoicesList);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: 'Failed to load invoices',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch all businesses
  const fetchBusinesses = async () => {
    if (!user) return;
    try {
      const businessesRef = collection(db, 'users', user.uid, 'businesses');
      const snapshot = await getDocs(businessesRef);
      
      const businessesList = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          businessId: doc.id,
          createdAt: data.createdAt?.toDate(),
        } as Business;
      });
      
      setBusinesses(businessesList);
    } catch (error) {
      console.error('Error fetching businesses:', error);
    }
  };

  // Get next invoice number
  const getNextInvoiceNumber = async (): Promise<string> => {
    if (!user) return 'INV-0001';
    
    try {
      const invoicesRef = collection(db, 'users', user.uid, 'invoices');
      const q = query(invoicesRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return 'INV-0001';
      }
      
      const lastInvoice = snapshot.docs[0].data();
      return generateInvoiceNumber(lastInvoice.invoiceNumber);
    } catch (error) {
      console.error('Error getting invoice number:', error);
      return 'INV-0001';
    }
  };

  // Create invoice
  const createInvoice = async (invoiceData: Omit<Invoice, 'invoiceId' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      console.log('Creating invoice with data:', invoiceData); // Debug log
      const invoicesRef = collection(db, 'users', user.uid, 'invoices');
      const docRef = await addDoc(invoicesRef, {
        ...invoiceData,
        issueDate: Timestamp.fromDate(invoiceData.issueDate),
        dueDate: Timestamp.fromDate(invoiceData.dueDate),
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      toast({
        title: 'Invoice Created',
        description: `Invoice ${invoiceData.invoiceNumber} has been created.`,
      });

      await fetchInvoices();
      return docRef.id;
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to create invoice',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Update invoice
  const updateInvoice = async (invoiceId: string, invoiceData: Partial<Invoice>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const invoiceRef = doc(db, 'users', user.uid, 'invoices', invoiceId);
      const updateData: any = { ...invoiceData, updatedAt: Timestamp.now() };

      // Convert dates to Timestamps
      if (invoiceData.issueDate) {
        updateData.issueDate = Timestamp.fromDate(invoiceData.issueDate);
      }
      if (invoiceData.dueDate) {
        updateData.dueDate = Timestamp.fromDate(invoiceData.dueDate);
      }

      await updateDoc(invoiceRef, updateData);

      toast({
        title: 'Invoice Updated',
        description: 'Invoice has been updated successfully.',
      });

      await fetchInvoices();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to update invoice',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Delete invoice
  const deleteInvoice = async (invoiceId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const invoiceRef = doc(db, 'users', user.uid, 'invoices', invoiceId);
      await deleteDoc(invoiceRef);

      toast({
        title: 'Invoice Deleted',
        description: 'Invoice has been deleted successfully.',
      });

      await fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete invoice',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Get single invoice
  const getInvoice = async (invoiceId: string): Promise<Invoice | null> => {
    if (!user) return null;

    try {
      const invoiceRef = doc(db, 'users', user.uid, 'invoices', invoiceId);
      const invoiceDoc = await getDoc(invoiceRef);

      if (invoiceDoc.exists()) {
        const data = invoiceDoc.data();
        return {
          ...data,
          invoiceId: invoiceDoc.id,
          issueDate: data.issueDate?.toDate(),
          dueDate: data.dueDate?.toDate(),
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        } as Invoice;
      }
      return null;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      return null;
    }
  };

  // Increment download count (with transaction to ensure limit)
  const incrementDownloadCount = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userEmail = user.email?.toLowerCase();
      
      const success = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          throw new Error('User document does not exist');
        }

        const userData = userDoc.data();
        const plan = userData.plan;
        
        // Pro users have unlimited downloads
        if (plan === 'pro') {
          return true;
        }

        const todayStr = new Date().toLocaleDateString('en-CA');
        const lastDownloadDate = userData.lastDownloadDate || '';
        const dailyDownloadCount = userData.dailyDownloadCount || 0;
        const todayDownloads = lastDownloadDate === todayStr ? dailyDownloadCount : 0;

        // Free users have unlimited downloads as well

        const newDailyDownloadCount = todayDownloads + 1;
        const newTotalDownloadCount = (userData.downloadCount || 0) + 1;

        // Update user document
        transaction.update(userRef, {
          downloadCount: newTotalDownloadCount,
          lastDownloadDate: todayStr,
          dailyDownloadCount: newDailyDownloadCount,
        });

        // Also update email download history to persist across account deletions
        if (userEmail) {
          const emailHistoryRef = doc(db, 'emailDownloadHistory', userEmail);
          transaction.set(emailHistoryRef, {
            email: userEmail,
            downloadCount: newTotalDownloadCount,
            lastDownloadDate: todayStr,
            dailyDownloadCount: newDailyDownloadCount,
            lastUpdated: Timestamp.now(),
          }, { merge: true });
        }

        return true;
      });

      return success;
    } catch (error) {
      console.error('Error incrementing download count:', error);
      return false;
    }
  };

  // Business operations
  const createBusiness = async (businessData: Omit<Business, 'businessId' | 'createdAt'>): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // If this business is being set as default, unset all other defaults first
      if (businessData.isDefault) {
        const businessesRef = collection(db, 'users', user.uid, 'businesses');
        const snapshot = await getDocs(businessesRef);
        const batch = writeBatch(db);
        
        snapshot.docs.forEach((doc) => {
          batch.update(doc.ref, { isDefault: false });
        });
        
        await batch.commit();
      }

      const businessesRef = collection(db, 'users', user.uid, 'businesses');
      const docRef = await addDoc(businessesRef, {
        ...businessData,
        createdAt: Timestamp.now(),
      });

      toast({
        title: 'Business Profile Created',
        description: 'Business profile has been created successfully.',
      });

      await fetchBusinesses();
      return docRef.id;
    } catch (error) {
      console.error('Error creating business:', error);
      toast({
        title: 'Error',
        description: 'Failed to create business profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateBusiness = async (businessId: string, businessData: Partial<Business>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // If this business is being set as default, unset all other defaults first
      if (businessData.isDefault) {
        const businessesRef = collection(db, 'users', user.uid, 'businesses');
        const snapshot = await getDocs(businessesRef);
        const batch = writeBatch(db);
        
        snapshot.docs.forEach((doc) => {
          if (doc.id !== businessId) {
            batch.update(doc.ref, { isDefault: false });
          }
        });
        
        await batch.commit();
      }

      const businessRef = doc(db, 'users', user.uid, 'businesses', businessId);
      await updateDoc(businessRef, businessData);

      toast({
        title: 'Business Profile Updated',
        description: 'Business profile has been updated successfully.',
      });

      await fetchBusinesses();
    } catch (error) {
      console.error('Error updating business:', error);
      toast({
        title: 'Error',
        description: 'Failed to update business profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteBusiness = async (businessId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const businessRef = doc(db, 'users', user.uid, 'businesses', businessId);
      await deleteDoc(businessRef);

      toast({
        title: 'Business Profile Deleted',
        description: 'Business profile has been deleted successfully.',
      });

      await fetchBusinesses();
    } catch (error) {
      console.error('Error deleting business:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete business profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Customer operations
  const fetchCustomers = async () => {
    if (!user) return;

    try {
      const customersRef = collection(db, 'users', user.uid, 'customers');
      const q = query(customersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const customersList: Customer[] = snapshot.docs.map(doc => ({
        customerId: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Customer[];
      
      setCustomers(customersList);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const createCustomer = async (customerData: Omit<Customer, 'customerId' | 'createdAt'>): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // If this customer is being set as default, unset all other defaults first
      if (customerData.isDefault) {
        const customersRef = collection(db, 'users', user.uid, 'customers');
        const snapshot = await getDocs(customersRef);
        const batch = writeBatch(db);
        
        snapshot.docs.forEach((doc) => {
          batch.update(doc.ref, { isDefault: false });
        });
        
        await batch.commit();
      }

      const customersRef = collection(db, 'users', user.uid, 'customers');
      const docRef = await addDoc(customersRef, {
        ...customerData,
        createdAt: Timestamp.now(),
      });

      toast({
        title: 'Customer Profile Created',
        description: 'Customer profile has been created successfully.',
      });

      await fetchCustomers();
      return docRef.id;
    } catch (error) {
      console.error('Error creating customer:', error);
      toast({
        title: 'Error',
        description: 'Failed to create customer profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateCustomer = async (customerId: string, customerData: Partial<Customer>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // If this customer is being set as default, unset all other defaults first
      if (customerData.isDefault) {
        const customersRef = collection(db, 'users', user.uid, 'customers');
        const snapshot = await getDocs(customersRef);
        const batch = writeBatch(db);
        
        snapshot.docs.forEach((doc) => {
          if (doc.id !== customerId) {
            batch.update(doc.ref, { isDefault: false });
          }
        });
        
        await batch.commit();
      }

      const customerRef = doc(db, 'users', user.uid, 'customers', customerId);
      await updateDoc(customerRef, customerData);

      toast({
        title: 'Customer Profile Updated',
        description: 'Customer profile has been updated successfully.',
      });

      await fetchCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast({
        title: 'Error',
        description: 'Failed to update customer profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteCustomer = async (customerId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const customerRef = doc(db, 'users', user.uid, 'customers', customerId);
      await deleteDoc(customerRef);

      toast({
        title: 'Customer Profile Deleted',
        description: 'Customer profile has been deleted successfully.',
      });

      await fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete customer profile',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Inventory item operations
  const fetchInventoryItems = async () => {
    if (!user) return;
    try {
      const itemsRef = collection(db, 'users', user.uid, 'inventory');
      const q = query(itemsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const itemsList = snapshot.docs.map(doc => ({
        itemId: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as InventoryItem[];
      setInventoryItems(itemsList);
    } catch (error) {
      console.error('Error fetching inventory items:', error);
    }
  };

  const createInventoryItem = async (itemData: Omit<InventoryItem, 'itemId' | 'createdAt'>): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    try {
      const itemsRef = collection(db, 'users', user.uid, 'inventory');
      const docRef = await addDoc(itemsRef, {
        ...itemData,
        createdAt: Timestamp.now(),
      });
      toast({
        title: 'Item / Service Created',
        description: 'Item or service has been added to inventory database.',
      });
      await fetchInventoryItems();
      return docRef.id;
    } catch (error) {
      console.error('Error creating inventory item:', error);
      toast({
        title: 'Error',
        description: 'Failed to create item or service',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const updateInventoryItem = async (itemId: string, itemData: Partial<InventoryItem>): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    try {
      const itemRef = doc(db, 'users', user.uid, 'inventory', itemId);
      await updateDoc(itemRef, itemData);
      toast({
        title: 'Item / Service Updated',
        description: 'Item or service details updated successfully.',
      });
      await fetchInventoryItems();
    } catch (error) {
      console.error('Error updating inventory item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item or service',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const deleteInventoryItem = async (itemId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');
    try {
      const itemRef = doc(db, 'users', user.uid, 'inventory', itemId);
      await deleteDoc(itemRef);
      toast({
        title: 'Item / Service Deleted',
        description: 'Item or service has been removed from inventory database.',
      });
      await fetchInventoryItems();
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete item or service',
        variant: 'destructive',
      });
      throw error;
    }
  };

  // Load data on mount
  useEffect(() => {
    if (user) {
      fetchInvoices();
      fetchBusinesses();
      fetchCustomers();
      fetchInventoryItems();
    } else {
      setInvoices([]);
      setBusinesses([]);
      setCustomers([]);
      setInventoryItems([]);
    }
  }, [user]);

  return {
    invoices,
    businesses,
    customers,
    inventoryItems,
    loading,
    fetchInvoices,
    fetchBusinesses,
    fetchCustomers,
    fetchInventoryItems,
    getNextInvoiceNumber,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    getInvoice,
    incrementDownloadCount,
    createBusiness,
    updateBusiness,
    deleteBusiness,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
  };
}
